import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Alert,
  Paper
} from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import { EvaluationRunner } from './EvaluationRunner';
import { TestCaseManager } from './TestCaseManager';
import { ResultsHistory } from './ResultsHistory';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`evaluation-tabpanel-${index}`}
      aria-labelledby={`evaluation-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface EvaluationViewProps {
  fetchWithAuth: (url: string, options?: any) => Promise<Response>;
}

export const EvaluationView: React.FC<EvaluationViewProps> = ({ fetchWithAuth }) => {
  const { t } = useLanguage();
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          {t('evaluation.devWarning')}
        </Typography>
      </Alert>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('evaluation.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('evaluation.description')}
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="evaluation tabs">
            <Tab label={t('evaluation.tabs.runEvaluation')} />
            <Tab label={t('evaluation.tabs.testCases')} />
            <Tab label={t('evaluation.tabs.resultsHistory')} />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <EvaluationRunner fetchWithAuth={fetchWithAuth} />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <TestCaseManager fetchWithAuth={fetchWithAuth} />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <ResultsHistory fetchWithAuth={fetchWithAuth} />
        </TabPanel>
      </Paper>
    </Container>
  );
};

